import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PaymentTermsSelector from '@/components/PaymentTermsSelector';
import { 
  ArrowLeft, Users, Clock, Flame, Snowflake, XCircle, Home, Plus, 
  Search, Phone, Mail, MapPin, Calendar, FileText, ShoppingCart,
  ChevronDown, ChevronUp, Edit, Trash2, X, Bell, BellRing, MessageSquare,
  LayoutGrid, List, Check, Trophy, PhoneCall, CalendarClock, Send, Settings, Tag, Building2, Download,
  Activity, ArrowRight, PoundSterling, MessageCircle
} from 'lucide-react';
import API_URL from '@/config/api';
import QuoteChooserModal from '@/components/leads/QuoteChooserModal';
import RevertToLeadModal from '@/components/leads/RevertToLeadModal';
import ConvertToProjectModal from '@/components/leads/ConvertToProjectModal';
import LeadFormModal from '@/components/leads/LeadFormModal';
import { useLeadDraft, useAutosaveDraft } from '@/hooks/useLeadDraft';
import { LeadDraftBanner } from '@/components/leads/LeadDraftBanner';

const DRAFT_FIELDS = ['customer_name', 'customer_email', 'customer_phone', 'customer_address', 'project_description', 'notes'];
import CallLogModal from '@/components/leads/CallLogModal';
import LeadDetailModal from '@/components/leads/LeadDetailModal';
import KanbanCard from '@/components/leads/KanbanCard';
import ListViewRow from '@/components/leads/ListViewRow';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';
import useHomePath from '@/hooks/useHomePath';
import { LEAD_SECTORS, SECTOR_BY_ID } from '@/config/leadSectors';

// NEW CRM Status configuration
const CRM_STATUSES = {
  new: { label: 'New Lead', color: '#10B981', bgColor: 'bg-emerald-100', textColor: 'text-emerald-800', borderColor: 'border-emerald-200', icon: Plus },
  contacted: { label: 'Contacted', color: '#3B82F6', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200', icon: PhoneCall },
  booked_in: { label: 'Booked In', color: '#8B5CF6', bgColor: 'bg-violet-100', textColor: 'text-violet-800', borderColor: 'border-violet-200', icon: CalendarClock },
  quoted: { label: 'Quoted', color: '#F59E0B', bgColor: 'bg-amber-100', textColor: 'text-amber-800', borderColor: 'border-amber-200', icon: FileText },
  won: { label: 'Won', color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-800', borderColor: 'border-green-200', icon: Trophy },
  lost: { label: 'Lost', color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-200', icon: XCircle }
};

const ACTIVE_STATUSES = Object.keys(CRM_STATUSES);

const ManageLeads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const homePath = useHomePath();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const installerIdParam = searchParams.get('installer');
  const actionParam = searchParams.get('action');
  const { isAuthenticated, isAdmin, isStaff } = useAuth();
  
  // Detect section from URL path (e.g., /probound/leads -> probound)
  const pathSection = location.pathname.split('/')[1]; // probound, proepoxy, or prodecor
  const sectionFilter = ['probound', 'proepoxy', 'prodecor'].includes(pathSection) ? pathSection : null;

  // Section-aware branding
  const SECTION_BRAND = {
    probound: {
      label: 'ProBound',
      tagline: 'Resin Bound · Resin Bonded',
      borderColor: '#2F5E96',
      logo: 'https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg',
    },
    proepoxy: {
      label: 'ProEpoxy',
      tagline: 'Epoxy Flooring Systems',
      borderColor: '#7A0B7A',
      logo: 'https://customer-assets.emergentagent.com/job_76625416-d43e-411a-b4d0-3b73c58f517c/artifacts/ta6bz3y6_IMG_8292.jpeg',
    },
    prodecor: {
      label: 'ProDecor',
      tagline: 'Decorative Walling',
      borderColor: '#D5AB09',
      logo: 'https://customer-assets.emergentagent.com/job_d9177fe4-220c-4669-9998-86c6e454a508/artifacts/4z707ot7_IMG_8289.jpeg',
    },
  };
  const brand = SECTION_BRAND[sectionFilter] || SECTION_BRAND.probound;

  // Quote chooser modal — opens when staff/installer clicks Quote Customer
  // on a lead that already has a quote on file.
  const [quoteChooserLead, setQuoteChooserLead] = useState(null);
  const openQuoteChooser = (lead) => {
    if (!lead.latest_quote_id) {
      // No prior quote — go straight to the builder
      navigate(`/quote-project?lead=${lead.id}`);
      return;
    }
    setQuoteChooserLead(lead);
  };
  
  // Clear unread "lead" notifications for this section when the page opens — so the
  // red badges on Home + the section hub drop the moment the installer reads them.
  useEffect(() => {
    if (!sectionFilter) return;
    fetch(`${API_URL}/api/notifications/mark-read-by-category/lead?service_type=${sectionFilter}`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  }, [sectionFilter]);
  
  // State
  const [leads, setLeads] = useState([]);
  // Map of { [leadId]: { postcode, territory, region, matches: [{user_id,name,strength}] } }.
  // Populated from `GET /api/leads/suggestions` — used to render the
  // "Suggested installers" chip on each Kanban card.
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState(sectionFilter || 'all');
  const [chipPicked, setChipPicked] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCallLogModal, setShowCallLogModal] = useState(false);
  const [activityTab, setActivityTab] = useState('timeline'); // 'timeline' | 'notes'
  const [activityEvents, setActivityEvents] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [installers, setInstallers] = useState([]);
  const [selectedInstallerId, setSelectedInstallerId] = useState(installerIdParam || '');
  const [newCallNote, setNewCallNote] = useState('');
  const [addingCallLog, setAddingCallLog] = useState(false);
  const [reminderLeads, setReminderLeads] = useState([]);
  const [showRemindersPanel, setShowRemindersPanel] = useState(false);
  // (drag-and-drop was removed — replaced with inline "Move to" dropdown on each card)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLead, setDetailLead] = useState(null);
  const [leadQuotes, setLeadQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    project_timeline: '',
    project_size: '',
    project_description: '',
    status: 'new',
    service_type: sectionFilter || 'probound',
    appointment_date: '',
    appointment_time: '',
    notes: '',
    installer_id: installerIdParam || '',
    reminder_date: '',
    reminder_note: '',
    source: ''
  });
  const { draft, save: saveDraft, clear: clearDraft } = useLeadDraft('ml_lead_draft', DRAFT_FIELDS);
  const addDirty = showAddModal && DRAFT_FIELDS.some((k) => (formData[k] || '').trim());
  useAutosaveDraft(addDirty, formData, saveDraft);

  // Fetch installers for staff
  useEffect(() => {
    if (isStaff) {
      fetchInstallers();
    }
  }, [isStaff]);

  // Open add modal if action=add
  useEffect(() => {
    if (actionParam === 'add' && installerIdParam) {
      setShowAddModal(true);
      setFormData(prev => ({ ...prev, installer_id: installerIdParam }));
    }
  }, [actionParam, installerIdParam]);

  const fetchInstallers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/installers`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInstallers(data);
      }
    } catch (error) {
      logger.error('Error fetching installers:', error);
    }
  };

  const fetchLeads = useCallback(async () => {
    try {
      let url = `${API_URL}/api/leads`;
      if (selectedInstallerId) {
        url += `?installer_id=${selectedInstallerId}`;
      }
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      logger.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedInstallerId]);

  // Fetch postcode-derived installer suggestions in one shot. Silent on
  // failure — the chip just won't show, so it never blocks the Kanban.
  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/suggestions`, { credentials: 'include' });
      if (response.ok) setSuggestions(await response.json());
    } catch (error) {
      logger.error('Error fetching lead suggestions:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      logger.error('Error fetching categories:', error);
    }
  }, []);

  const fetchReminderLeads = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/reminders/today`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReminderLeads(data);
      }
    } catch (error) {
      logger.error('Error fetching reminder leads:', error);
    }
  }, []);

  // Fetch leads and categories when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
      fetchReminderLeads();
      fetchCategories();
      fetchSuggestions();
    }
  }, [isAuthenticated, fetchLeads, fetchReminderLeads, fetchCategories, fetchSuggestions]);

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      const leadData = { ...formData };
      if (isStaff && formData.installer_id) {
        leadData.installer_id = formData.installer_id;
      }
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(leadData)
      });
      if (response.ok) {
        clearDraft();
        setShowAddModal(false);
        resetForm();
        fetchLeads();
        if (installerIdParam && actionParam === 'add') {
          navigate('/staff');
        }
      }
    } catch (error) {
      logger.error('Error adding lead:', error);
    }
  };

  const handleUpdateLead = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/leads/${selectedLead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowEditModal(false);
        setSelectedLead(null);
        resetForm();
        fetchLeads();
        fetchReminderLeads();
      }
    } catch (error) {
      logger.error('Error updating lead:', error);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchLeads();
      }
    } catch (error) {
      logger.error('Error updating lead status:', error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchLeads();
      }
    } catch (error) {
      logger.error('Error deleting lead:', error);
    }
  };

  const handleArchiveLead = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'archived' })
      });
      if (response.ok) {
        fetchLeads();
      }
    } catch (error) {
      logger.error('Error archiving lead:', error);
    }
  };

  const handleRestoreLead = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'new' })
      });
      if (response.ok) {
        fetchLeads();
      }
    } catch (error) {
      logger.error('Error restoring lead:', error);
    }
  };

  const handleAddCallLog = async () => {
    if (!newCallNote.trim() || !selectedLead) return;
    
    setAddingCallLog(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/${selectedLead.id}/call-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ note: newCallNote })
      });
      if (response.ok) {
        setNewCallNote('');
        fetchLeads();
        // Update the selected lead with new call log
        const updatedLead = leads.find(l => l.id === selectedLead.id);
        if (updatedLead) {
          const result = await response.json();
          setSelectedLead({
            ...updatedLead,
            call_logs: [...(updatedLead.call_logs || []), result.call_log]
          });
        }
        // Refresh unified activity timeline
        fetchActivity(selectedLead.id);
      }
    } catch (error) {
      logger.error('Error adding call log:', error);
    } finally {
      setAddingCallLog(false);
    }
  };

  const handleClearReminder = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/clear-reminder`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchLeads();
        fetchReminderLeads();
      }
    } catch (error) {
      logger.error('Error clearing reminder:', error);
    }
  };

  const [convertLead, setConvertLead] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertProjectValue, setConvertProjectValue] = useState('');
  const [convertPaymentTerms, setConvertPaymentTerms] = useState(null);
  const [convertPaymentTermsValid, setConvertPaymentTermsValid] = useState(true);

  // Revert "Won" lead back to a funnel stage (deletes the project)
  const [revertLead, setRevertLead] = useState(null);          // lead being reverted
  const [revertStep, setRevertStep] = useState('confirm');     // 'confirm' | 'pick-status'
  const [revertChosenStatus, setRevertChosenStatus] = useState(null);
  const [reverting, setReverting] = useState(false);

  const openRevertModal = (lead) => {
    setRevertLead(lead);
    setRevertStep('confirm');
    setRevertChosenStatus(null);
  };

  const closeRevertModal = () => {
    if (reverting) return;
    setRevertLead(null);
    setRevertStep('confirm');
    setRevertChosenStatus(null);
  };

  const confirmRevertToLead = async () => {
    if (!revertLead || !revertChosenStatus) return;
    const projectId = revertLead.project_id;
    if (!projectId) {
      alert('No linked project found — please refresh and try again.');
      return;
    }
    setReverting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/projects/${projectId}?revert_lead_status=${encodeURIComponent(revertChosenStatus)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to revert');
      }
      setRevertLead(null);
      setRevertStep('confirm');
      setRevertChosenStatus(null);
      await fetchLeads();
    } catch (e) {
      alert(e.message);
    } finally {
      setReverting(false);
    }
  };

  const handleConvertToProject = (lead) => {
    setConvertLead(lead);
    // Pre-fill from the lead's latest quote if available
    setConvertProjectValue(lead.latest_quote_total ? String(lead.latest_quote_total) : '');
    setConvertPaymentTerms(null);  // populated after the quote loads below
    if (lead.latest_quote_id) {
      fetch(`${API_URL}/api/project-quotes/${lead.latest_quote_id}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((q) => {
          if (!q) return;
          if (q.grand_total && !convertProjectValue) setConvertProjectValue(String(q.grand_total));
          if (q.payment_terms) setConvertPaymentTerms(q.payment_terms);
        })
        .catch(() => {});
    }
  };

  const confirmConvertToProject = async () => {
    if (!convertLead) return;
    const value = parseFloat(convertProjectValue);
    if (!value || value <= 0) { alert('Enter a project value (inc VAT)'); return; }
    if (!convertPaymentTermsValid) { alert('Payment terms must total 100% and every stage needs a label'); return; }
    setConverting(true);
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lead_id: convertLead.id,
          project_value: value,
          payment_terms: convertPaymentTerms,
          source_quote_id: convertLead.latest_quote_id || null,
        }),
      });

      if (response.ok) {
        const project = await response.json();
        setConvertLead(null);
        setConvertProjectValue('');
        setConvertPaymentTerms(null);
        navigate(`/projects/${project.id}`);
      } else {
        const error = await response.json();
        alert(formatApiError(error) || 'Failed to convert to project');
      }
    } catch (error) {
      logger.error('Error converting to project:', error);
      alert('Failed to convert to project');
    } finally {
      setConverting(false);
    }
  };

  const handleSendReminderEmail = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/send-reminder-email`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        alert('Reminder email sent successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to send email: ${error.detail}`);
      }
    } catch (error) {
      logger.error('Error sending reminder email:', error);
      alert('Failed to send email');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_address: '',
      project_timeline: '',
      project_size: '',
      project_description: '',
      status: 'new',
      service_type: categories.length > 0 ? categories[0].id : 'probound',
      appointment_date: '',
      appointment_time: '',
      notes: '',
      installer_id: installerIdParam || '',
      reminder_date: '',
      reminder_note: '',
      source: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    if (draft) { setFormData((f) => ({ ...f, ...draft.data, installer_id: f.installer_id || draft.data.installer_id || '' }));  }
    setShowAddModal(true);
  };

  const openEditModal = (lead) => {
    setSelectedLead(lead);
    setFormData({
      customer_name: lead.customer_name || '',
      customer_email: lead.customer_email || '',
      customer_phone: lead.customer_phone || '',
      customer_address: lead.customer_address || '',
      project_timeline: lead.project_timeline || '',
      project_size: lead.project_size || '',
      project_description: lead.project_description || '',
      status: lead.status || 'new',
      service_type: lead.service_type || 'probound',
      appointment_date: lead.appointment_date || '',
      appointment_time: lead.appointment_time || '',
      notes: lead.notes || '',
      installer_id: lead.installer_id || '',
      reminder_date: lead.reminder_date || '',
      reminder_note: lead.reminder_note || '',
      source: lead.source || ''
    });
    setShowEditModal(true);
  };

  const openCallLogModal = (lead) => {
    setSelectedLead(lead);
    setNewCallNote('');
    setActivityTab('timeline');
    setActivityEvents([]);
    setShowCallLogModal(true);
    fetchActivity(lead.id);
  };

  const fetchActivity = async (leadId) => {
    setLoadingActivity(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/${leadId}/activity`, { credentials: 'include' });
      if (res.ok) setActivityEvents(await res.json());
    } finally {
      setLoadingActivity(false);
    }
  };

  const openDetailModal = (lead) => {
    // 1c: once a lead is converted to a project (status='won'), all lead-level
    // interactions are locked out — including the detail modal. Clicking the
    // customer name no-ops; the ConvertedCard surfaces the two allowed actions.
    if (lead?.status === 'won') return;
    setDetailLead(lead);
    setShowDetailModal(true);
    fetchLeadQuotes(lead.id);
    // Clear any unread notifications for this lead
    fetch(`${API_URL}/api/notifications/mark-read-by-lead/${lead.id}`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  };

  const deepLinkLeadId = searchParams.get('lead');
  const deepLinkOpened = useRef(false);
  useEffect(() => {
    if (!deepLinkLeadId || deepLinkOpened.current || !leads.length) return;
    const target = leads.find((l) => l.id === deepLinkLeadId);
    if (!target) return;
    deepLinkOpened.current = true;
    if (target.service_type && target.service_type !== serviceFilter) { setServiceFilter('all'); setChipPicked(true); }
    openDetailModal(target);
  }, [deepLinkLeadId, leads]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeadQuotes = async (leadId) => {
    setLoadingQuotes(true);
    try {
      const response = await fetch(`${API_URL}/api/project-quotes?lead_id=${leadId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        setLeadQuotes(await response.json());
      }
    } catch (error) {
      logger.error('Error fetching quotes:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const handleDownloadQuotePDF = async (quote) => {
    try {
      const payload = {
        customer_name: quote.customer_name || null,
        customer_address: quote.customer_address || null,
        customer_phone: quote.customer_phone || null,
        installer_name: quote.installer_name || null,
        company_name: quote.company_name || null,
        project_type: quote.quote_data?.project_type || null,
        quote_date: quote.created_at ? quote.created_at.split('T')[0] : null,
        surfacing_items: quote.quote_data?.surfacing_items || [],
        additional_costs: quote.quote_data?.additional_costs || {},
        edgings: quote.quote_data?.edgings || [],
        walling_fencing_items: quote.quote_data?.walling_fencing_items || [],
        misc_items: quote.quote_data?.misc_items || [],
        totals: quote.quote_data?.totals || {}
      };
      
      const response = await fetch(`${API_URL}/api/quotes/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition');
        const filename = disposition ? disposition.split('filename=')[1] : `${quote.reference || 'quote'}.pdf`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      logger.error('Error downloading PDF:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = CRM_STATUSES[status] || CRM_STATUSES.new;
    return `${config.bgColor} ${config.textColor} ${config.borderColor}`;
  };

  const getServiceBadge = (serviceType) => {
    // Find category from dynamic categories
    const category = categories.find(c => c.id === serviceType);
    if (category) {
      return { label: category.name, color: category.color };
    }
    const sector = SECTOR_BY_ID[serviceType];
    if (sector) return { label: sector.label, color: sector.color };
    // Fallback for unknown categories
    return { label: serviceType || 'Unknown', color: '#6B7280' };
  };

  const getStatusIcon = (status) => {
    const config = CRM_STATUSES[status] || CRM_STATUSES.new;
    const Icon = config.icon;
    return <Icon className="w-4 h-4" />;
  };

  const getStatusLabel = (status) => {
    return CRM_STATUSES[status]?.label || status;
  };

  // Filter leads
  // Separate active and archived leads
  const activeLeads = leads.filter(lead => lead.status !== 'archived');
  const archivedLeads = leads.filter(lead => lead.status === 'archived');

  const filteredLeads = (showArchived ? archivedLeads : activeLeads).filter(lead => {
    const matchesSearch = lead.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.customer_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.customer_phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesService = serviceFilter === 'all' || lead.service_type === serviceFilter
      || (!chipPicked && (lead.service_type || 'general') === 'general');
    return matchesSearch && matchesStatus && matchesService;
  });

  // Group leads by status for Kanban view
  const leadsByStatus = ACTIVE_STATUSES.reduce((acc, status) => {
    acc[status] = filteredLeads.filter(lead => lead.status === status);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    booked_in: leads.filter(l => l.status === 'booked_in').length,
    quoted: leads.filter(l => l.status === 'quoted').length,
    won: leads.filter(l => l.status === 'won').length,
    lost: leads.filter(l => l.status === 'lost').length,
    // Dynamic category counts
    ...categories.reduce((acc, cat) => {
      acc[cat.id] = leads.filter(l => l.service_type === cat.id).length;
      return acc;
    }, {})
  };

  // Drag-and-drop was removed in favour of an inline status dropdown on each
  // card — it's faster to click "Move to → Contacted" than to drag a card
  // 700px across the board, especially with the new horizontal scroll.

  const fmtCurrency = (n) => {
    if (n == null || n === '') return null;
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(Number(n));
    } catch { return null; }
  };

  // Kanban View — twin scrollbar pattern: a thin rail above mirrors the
  // board's horizontal scroll. Means desktop users can scroll the columns
  // without first scrolling down past 400px of cards to reach the bar.
  const topScrollRef = useRef(null);
  const boardScrollRef = useRef(null);
  const [innerWidth, setInnerWidth] = useState(0);
  const syncingRef = useRef(false);

  const syncFrom = (source, target) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (target) target.scrollLeft = source.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  useLayoutEffect(() => {
    // Track the board's inner content width so the top rail's spacer matches
    // exactly — otherwise the rail's max-scroll wouldn't reach the board's.
    const board = boardScrollRef.current;
    if (!board) return;
    const measure = () => {
      const w = board.firstElementChild?.scrollWidth || board.scrollWidth;
      setInnerWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(board);
    if (board.firstElementChild) ro.observe(board.firstElementChild);
    return () => ro.disconnect();
  }, [leadsByStatus, viewMode]); // re-measure when data or view changes

  // eslint-disable-next-line react/no-unstable-nested-components
  const KanbanView = () => (
    <div>
      {/* Twin scrollbar: visible only when the board actually overflows.
          Custom scrollbar styling so it shows on all platforms even when the
          OS uses "overlay" scrollbars by default. */}
      <div
        ref={topScrollRef}
        onScroll={(e) => syncFrom(e.currentTarget, boardScrollRef.current)}
        className="kanban-top-scroll mb-2"
        data-testid="kanban-top-scroll"
        aria-hidden="true"
      >
        <div style={{ width: innerWidth || 1, height: 1 }} />
      </div>
      <div
        ref={boardScrollRef}
        onScroll={(e) => syncFrom(e.currentTarget, topScrollRef.current)}
        className="overflow-x-auto pb-4"
      >
        <div className="flex gap-4 min-w-max">
        {Object.entries(CRM_STATUSES).map(([status, config]) => (
          <div
            key={status}
            className="w-72 flex-shrink-0"
            data-testid={`kanban-column-${status}`}
          >
            <div 
              className="rounded-t-lg px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: config.color }}
            >
              <div className="flex items-center gap-2 text-white">
                {React.createElement(config.icon, { className: "w-4 h-4" })}
                <span className="font-semibold text-sm">{config.label}</span>
              </div>
              <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {leadsByStatus[status]?.length || 0}
              </span>
            </div>
            <div className="bg-gray-100 rounded-b-lg p-3 min-h-[400px] space-y-3">
              {leadsByStatus[status]?.map(lead => (
                <KanbanCard
                  key={lead.id}
                  lead={lead}
                  suggestion={suggestions[lead.id]}
                  getServiceBadge={getServiceBadge}
                  fmtCurrency={fmtCurrency}
                  navigate={navigate}
                  openRevertModal={openRevertModal}
                  openDetailModal={openDetailModal}
                  openCallLogModal={openCallLogModal}
                  openEditModal={openEditModal}
                  openQuoteChooser={openQuoteChooser}
                  handleConvertToProject={handleConvertToProject}
                  handleArchiveLead={handleArchiveLead}
                  handleStatusChange={handleStatusChange}
                  statusOptions={Object.entries(CRM_STATUSES)}
                />
              ))}
              {leadsByStatus[status]?.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No leads
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {/* Header - Hidden in embed mode */}
      {!isEmbedMode && (
        <header className="bg-white shadow-sm border-b-4 border-[#006400]">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              onClick={() => navigate(sectionFilter ? `/${sectionFilter}` : homePath)}
              variant="outline"
              className="border-2 border-[#006400] text-[#006400] hover:bg-[#006400]/10 font-medium"
              data-testid="header-back-to-portal"
            >
              <Home className="w-4 h-4 mr-2" />
              {sectionFilter ? 'Back' : 'Back to Portal'}
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_b4127cdf-1323-436d-9b7a-27a06c6d6f4a/artifacts/bwvyspsq_Logo_-_Ox.png" 
              alt="Ox Resin" 
              className="h-12 md:h-14 cursor-pointer object-contain"
              onClick={() => navigate(homePath)}
            />
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {draft && !showAddModal && <div className="mb-4"><LeadDraftBanner draft={draft} onResume={openAddModal} onDiscard={clearDraft} /></div>}
        {/* Section Title & View Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#27292E]">Lead CRM</h1>
            <p className="text-sm text-gray-500 mt-1">Track your leads through the sales pipeline</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap [&>*]:shrink-0">
            {/* Coverage Editor — quick jump so staff can tune the
                postcode → territory map without hunting through Settings. */}
            <button
              onClick={() => navigate('/staff/postcode-map')}
              className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg px-2.5 py-1.5"
              data-testid="open-postcode-map-btn"
              title="Edit which postcode districts belong to each territory"
            >
              <MapPin className="w-3.5 h-3.5" />
              Coverage map
            </button>

            {/* Reminders Button */}
            {reminderLeads.length > 0 && (
              <button
                onClick={() => setShowRemindersPanel(!showRemindersPanel)}
                className="relative p-2 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                data-testid="reminders-btn"
              >
                <BellRing className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {reminderLeads.length}
                </span>
              </button>
            )}
            
            {/* View Toggle */}
            <div className="flex bg-white rounded-lg border-2 border-gray-200 p-1">
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'kanban' ? 'bg-[#006400] text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="kanban-view-btn"
              >
                <LayoutGrid className="w-4 h-4" />
                Kanban
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' ? 'bg-[#006400] text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
                List
              </button>
            </div>

            {/* Archive Toggle */}
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border-2 ${
                showArchived 
                  ? 'bg-gray-700 text-white border-gray-700' 
                  : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="archive-toggle-btn"
            >
              <XCircle className="w-4 h-4" />
              Archive {archivedLeads.length > 0 && `(${archivedLeads.length})`}
            </button>
            
            <Button
              onClick={openAddModal}
              className="bg-[#006400] hover:bg-[#004d00] text-white"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Reminders Panel */}
        {showRemindersPanel && reminderLeads.length > 0 && (
          <div className="mb-6 bg-amber-50 rounded-xl border-2 border-amber-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BellRing className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-800">Due Reminders ({reminderLeads.length})</h3>
              </div>
              <button onClick={() => setShowRemindersPanel(false)} className="text-amber-600 hover:text-amber-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reminderLeads.map(lead => (
                <div key={lead.id} className="bg-white p-3 rounded-lg border border-amber-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-sm">{lead.customer_name}</h4>
                      <p className="text-xs text-gray-500">{lead.reminder_note || 'Follow up required'}</p>
                    </div>
                    <span className="text-xs text-amber-600 font-medium">{lead.reminder_date}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleSendReminderEmail(lead.id)}
                      size="sm"
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Email
                    </Button>
                    <Button
                      onClick={() => handleClearReminder(lead.id)}
                      size="sm"
                      variant="outline"
                      className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-100 text-xs"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Done
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section Filter Chips */}
        <div className="pill-row items-center mb-5" data-testid="section-filter-chips">
          <span className="text-xs uppercase tracking-wide font-semibold text-gray-500 mr-1 shrink-0">Section:</span>
          {[{ id: 'all', label: 'All', color: '#27292E' }, ...LEAD_SECTORS].map(s => {
            const isActive = serviceFilter === s.id;
            return (
              <button
                key={s.id}
                onClick={() => { setServiceFilter(s.id); setChipPicked(true); }}
                data-testid={`section-filter-${s.id}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                  isActive ? 'text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: s.color, borderColor: s.color } : { borderColor: '#e5e7eb' }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-6">
          <button
            onClick={() => setStatusFilter('all')}
            className={`bg-white rounded-lg p-3 shadow-sm border-2 transition-all text-center ${
              statusFilter === 'all' ? 'border-[#006400]' : 'border-gray-100 hover:border-gray-300'
            }`}
          >
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-xl font-bold text-[#27292E]">{stats.total}</p>
          </button>
          {Object.entries(CRM_STATUSES).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
              className={`bg-white rounded-lg p-3 shadow-sm border-2 transition-all text-center ${
                statusFilter === key ? `border-2` : 'border-gray-100 hover:border-gray-300'
              }`}
              style={{ borderColor: statusFilter === key ? config.color : undefined }}
            >
              <p className="text-xs text-gray-500">{config.label}</p>
              <p className="text-xl font-bold" style={{ color: config.color }}>{stats[key]}</p>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, address, or phone..."
              className="pl-10 h-12 border-2"
            />
          </div>
        </div>

        {/* Main View */}
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-500">
            Loading leads...
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No leads found</p>
            <Button
              onClick={openAddModal}
              className="mt-4 bg-[#006400] hover:bg-[#004d00] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Lead
            </Button>
          </div>
        ) : showArchived ? (
          /* Archived Leads View */
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#27292E]">Archived Leads</h2>
              <p className="text-sm text-gray-500">{filteredLeads.length} archived lead{filteredLeads.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="divide-y divide-gray-100">
              {filteredLeads.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No archived leads</div>
              ) : filteredLeads.map((lead) => (
                <div key={lead.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <h4 className="font-semibold text-gray-900">{lead.customer_name}</h4>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      {lead.customer_phone && <span>{lead.customer_phone}</span>}
                      {lead.customer_address && <span>{lead.customer_address}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleRestoreLead(lead.id)}
                      variant="outline"
                      className="text-sm border-green-300 text-green-700 hover:bg-green-50"
                      data-testid={`restore-lead-${lead.id}`}
                    >
                      Restore
                    </Button>
                    <Button
                      onClick={() => handleDeleteLead(lead.id)}
                      variant="outline"
                      className="text-sm border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : viewMode === 'kanban' ? (
          <KanbanView />
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#27292E]">
                {serviceFilter !== 'all' ? `${getServiceBadge(serviceFilter).label} ` : ''}
                {statusFilter === 'all' ? 'All Leads' : `${CRM_STATUSES[statusFilter]?.label || ''} Leads`}
              </h2>
              <p className="text-sm text-gray-500">{filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="divide-y divide-gray-100">
              {filteredLeads.map((lead) => (
                <ListViewRow
                  key={lead.id}
                  lead={lead}
                  expandedLead={expandedLead}
                  setExpandedLead={setExpandedLead}
                  loadingQuotes={loadingQuotes}
                  leadQuotes={leadQuotes}
                  isStaff={isStaff}
                  crmStatuses={CRM_STATUSES}
                  getServiceBadge={getServiceBadge}
                  getStatusBadge={getStatusBadge}
                  getStatusIcon={getStatusIcon}
                  getStatusLabel={getStatusLabel}
                  fmtCurrency={fmtCurrency}
                  navigate={navigate}
                  openDetailModal={openDetailModal}
                  openCallLogModal={openCallLogModal}
                  openEditModal={openEditModal}
                  openRevertModal={openRevertModal}
                  handleStatusChange={handleStatusChange}
                  handleConvertToProject={handleConvertToProject}
                  handleArchiveLead={handleArchiveLead}
                  handleDownloadQuotePDF={handleDownloadQuotePDF}
                  handleSendReminderEmail={handleSendReminderEmail}
                  handleClearReminder={handleClearReminder}
                  setShowDetailModal={setShowDetailModal}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Add Lead Modal */}
      <LeadFormModal
        open={showAddModal}
        mode="add"
        formData={formData}
        setFormData={setFormData}
        installers={installers}
        isStaff={isStaff}
        crmStatuses={CRM_STATUSES}
        onSubmit={handleAddLead}
        onClose={() => {
          const typed = ['customer_name', 'customer_email', 'customer_phone', 'customer_address', 'project_description', 'notes'].some((k) => (formData[k] || '').trim());
          if (typed && !window.confirm("Discard this lead?\n\nYou've started filling in the form — closing it now will lose what you've typed.\n\nTip: closing the tab or reloading instead keeps it saved as a draft.")) return;
          clearDraft(); setShowAddModal(false); resetForm();
        }}
      />

      {/* Edit Lead Modal */}
      <LeadFormModal
        open={showEditModal}
        mode="edit"
        formData={formData}
        setFormData={setFormData}
        installers={installers}
        isStaff={isStaff}
        crmStatuses={CRM_STATUSES}
        onSubmit={handleUpdateLead}
        onClose={() => { setShowEditModal(false); setSelectedLead(null); resetForm(); }}
      />

      {/* Call Log Modal */}
      <CallLogModal
        open={showCallLogModal}
        lead={selectedLead}
        activityTab={activityTab}
        setActivityTab={setActivityTab}
        activityEvents={activityEvents}
        loadingActivity={loadingActivity}
        newCallNote={newCallNote}
        setNewCallNote={setNewCallNote}
        addingCallLog={addingCallLog}
        onClose={() => { setShowCallLogModal(false); setSelectedLead(null); setNewCallNote(''); }}
        onAddNote={handleAddCallLog}
      />

      {/* Lead Detail Modal */}
      <LeadDetailModal
        open={showDetailModal}
        lead={detailLead}
        leadQuotes={leadQuotes}
        serviceBadge={getServiceBadge}
        statusBadge={getStatusBadge}
        statusIcon={getStatusIcon}
        statusLabel={getStatusLabel}
        onClose={() => { setShowDetailModal(false); setDetailLead(null); }}
        onOpenCallLog={() => { setShowDetailModal(false); openCallLogModal(detailLead); }}
        onSendQuote={() => { setShowDetailModal(false); navigate(`/quote-project?lead=${detailLead.id}`); }}
        onOpenOrder={() => { setShowDetailModal(false); navigate(`/order?lead=${detailLead.id}`); }}
        onEdit={() => { setShowDetailModal(false); openEditModal(detailLead); }}
        onDownloadQuotePDF={handleDownloadQuotePDF}
      />

      {/* Convert to Project Confirmation Modal */}
      <ConvertToProjectModal
        lead={convertLead}
        projectValue={convertProjectValue}
        setProjectValue={setConvertProjectValue}
        paymentTerms={convertPaymentTerms}
        setPaymentTerms={setConvertPaymentTerms}
        paymentTermsValid={convertPaymentTermsValid}
        setPaymentTermsValid={setConvertPaymentTermsValid}
        converting={converting}
        onCancel={() => setConvertLead(null)}
        onConfirm={confirmConvertToProject}
      />

      {/* Quote chooser modal */}
      <QuoteChooserModal
        lead={quoteChooserLead}
        onClose={() => setQuoteChooserLead(null)}
        onEditOrRevise={() => {
          navigate(`/quote-project?quote=${quoteChooserLead.latest_quote_id}`);
          setQuoteChooserLead(null);
        }}
        onFresh={() => {
          navigate(`/quote-project?lead=${quoteChooserLead.id}&fresh=true`);
          setQuoteChooserLead(null);
        }}
      />

      {/* Revert "Won" lead back to a funnel stage (deletes the project) */}
      <RevertToLeadModal
        lead={revertLead}
        step={revertStep}
        chosenStatus={revertChosenStatus}
        onPickStatus={setRevertChosenStatus}
        reverting={reverting}
        onClose={closeRevertModal}
        onAdvanceToPick={() => setRevertStep('pick-status')}
        onConfirm={confirmRevertToLead}
        fmtCurrency={fmtCurrency}
      />

    </div>
  );
};

export default ManageLeads;
